import type { ToolExecutionConfig } from "@/queries/tool-registry/types";

/** Recursively sort object keys so diffs aren't noisy when storage reorders JSON (Postgres
 * JSONB doesn't preserve insertion order). */
const sortKeysDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  if (value !== null && typeof value === "object") {
    const sorted: Record<string, unknown> = {};
    Object.keys(value as Record<string, unknown>)
      .sort()
      .forEach(key => {
        sorted[key] = sortKeysDeep((value as Record<string, unknown>)[key]);
      });
    return sorted;
  }
  return value;
};

/** Pretty-print an execution config for diff display; placeholder when schema-only.
 * Keys sorted so reordered storage doesn't show as fake changes. */
export const stringifyExecution = (exec: ToolExecutionConfig | null | undefined): string => {
  if (!exec) {
    return "(no function body)";
  }
  return JSON.stringify(sortKeysDeep(exec), null, 2);
};

/** Pretty-print a tool definition for diff display. Same key-sort hygiene. */
export const stringifyToolDefinition = (def: unknown): string => {
  if (!def) {
    return "";
  }
  return JSON.stringify(sortKeysDeep(def), null, 2);
};

/** Pretty-print a response schema for diff display; placeholder when unset. Same key-sort hygiene. */
export const stringifyResponseSchema = (
  schema: Record<string, unknown> | null | undefined,
): string => {
  if (!schema) {
    return "(no response schema)";
  }
  return JSON.stringify(sortKeysDeep(schema), null, 2);
};
